/**
 * Terms of Service Page
 */

import { Link } from 'react-router-dom';
import { FileText, AlertTriangle, Shield, Info } from 'lucide-react';

export default function TermsPage(): JSX.Element {
  return (
    <div className="container-custom py-12">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
              <FileText size={32} className="text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100">
                Terms of Service
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                Last updated: {new Date().toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>

        {/* Important Notice */}
        <div className="card p-6 bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-200 dark:border-amber-800 mb-8">
          <div className="flex items-start gap-4">
            <AlertTriangle size={24} className="text-amber-700 dark:text-amber-400 flex-shrink-0 mt-1" />
            <div>
              <h2 className="text-lg font-bold text-amber-900 dark:text-amber-200 mb-2">
                Important Notice
              </h2>
              <p className="text-sm text-amber-800 dark:text-amber-300">
                By accessing or using Foxie Systems, you agree to be bound by these Terms of Service.
                If you do not agree to these terms, please do not use our services.
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="card p-8 space-y-8">
          {/* Section 1 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
              <Info size={24} className="text-primary-600 dark:text-primary-400" />
              1. Nature of Services
            </h2>
            <div className="prose dark:prose-invert max-w-none">
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Foxie Systems is a digital marketplace that sells <strong>informational and educational content</strong> related to gambling and betting strategies.
              </p>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                <strong>We are NOT a gambling platform.</strong> We do not:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-4">
                <li>Operate gambling or betting services</li>
                <li>Accept bets or wagers</li>
                <li>Facilitate any wagering activities</li>
                <li>Hold betting funds</li>
                <li>Guarantee any outcomes or winnings</li>
              </ul>
            </div>
          </section>

          {/* Section 2 */}
          <section className="pt-6 border-t border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              2. Product Disclaimers
            </h2>
            <div className="prose dark:prose-invert max-w-none">
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                All products sold on this platform are for <strong>educational and entertainment purposes only</strong>.
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-4">
                <li><strong>No Guaranteed Results:</strong> We do not guarantee winnings, profits, or any specific outcomes. Past performance does not guarantee future results.</li>
                <li><strong>Not Financial Advice:</strong> Our content is not financial, legal, or professional advice. You are solely responsible for your own decisions.</li>
                <li><strong>Risk Acknowledgment:</strong> Gambling involves risk. You may lose money. Never gamble more than you can afford to lose.</li>
              </ul>
            </div>
          </section>

          {/* Section 3 */}
          <section className="pt-6 border-t border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              3. User Eligibility
            </h2>
            <div className="prose dark:prose-invert max-w-none">
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                To use Foxie Systems, you must:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-4">
                <li>Be at least <strong>18 years old</strong></li>
                <li>Comply with all local laws regarding gambling-related content in your jurisdiction</li>
                <li>Use the content legally and responsibly</li>
                <li>Not use the content for illegal purposes</li>
              </ul>
            </div>
          </section>

          {/* Section 4 */}
          <section className="pt-6 border-t border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
              <Shield size={24} className="text-green-600 dark:text-green-400" />
              4. Digital Products & Delivery
            </h2>
            <div className="prose dark:prose-invert max-w-none">
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                All products are digital downloads delivered instantly upon purchase completion.
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-4">
                <li><strong>Delivery:</strong> Products are available for download immediately after payment.</li>
                <li><strong>Access:</strong> You can access your purchased products from your account dashboard.</li>
                <li><strong>License:</strong> You receive a personal, non-transferable license to use the content.</li>
                <li><strong>Restrictions:</strong> You may not resell, redistribute, or share the content with others.</li>
              </ul>
            </div>
          </section>

          {/* Section 5 */}
          <section className="pt-6 border-t border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              5. Refund Policy
            </h2>
            <div className="prose dark:prose-invert max-w-none">
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Due to the digital nature of our products:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-4">
                <li>Refunds are available <strong>before you download the product</strong></li>
                <li>Once a product has been successfully downloaded, <strong>refunds are locked</strong></li>
                <li>Refund requests must be submitted within 7 days of purchase</li>
                <li>See our <Link to="/refund-policy" className="text-primary-600 dark:text-primary-400 hover:underline">Refund Policy</Link> for full details</li>
              </ul>
            </div>
          </section>

          {/* Section 6 */}
          <section className="pt-6 border-t border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              6. Payment & Pricing
            </h2>
            <div className="prose dark:prose-invert max-w-none">
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-4">
                <li>All prices are listed in the displayed currency</li>
                <li>Prices are subject to change without notice</li>
                <li>Payment is processed securely through our payment providers (Stripe, PayPal, etc.)</li>
                <li>You agree to provide accurate payment information</li>
              </ul>
            </div>
          </section>

          {/* Section 7 */}
          <section className="pt-6 border-t border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              7. Intellectual Property
            </h2>
            <div className="prose dark:prose-invert max-w-none">
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                All content on Foxie Systems, including but not limited to text, graphics, logos, and digital products, is protected by intellectual property laws.
              </p>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                You may not copy, reproduce, distribute, or create derivative works without explicit permission.
              </p>
            </div>
          </section>

          {/* Section 8 */}
          <section className="pt-6 border-t border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              8. Limitation of Liability
            </h2>
            <div className="prose dark:prose-invert max-w-none">
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                To the maximum extent permitted by law:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-4">
                <li>Foxie Systems is not liable for any losses resulting from use of our content</li>
                <li>We provide content "as is" without warranties of any kind</li>
                <li>We are not responsible for decisions made based on our content</li>
                <li>Our total liability shall not exceed the amount you paid for the product</li>
              </ul>
            </div>
          </section>

          {/* Section 9 */}
          <section className="pt-6 border-t border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              9. Changes to Terms
            </h2>
            <div className="prose dark:prose-invert max-w-none">
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                We reserve the right to modify these Terms of Service at any time. Changes will be effective immediately upon posting. Your continued use of the platform constitutes acceptance of modified terms.
              </p>
            </div>
          </section>

          {/* Section 10 */}
          <section className="pt-6 border-t border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              10. Contact Us
            </h2>
            <div className="prose dark:prose-invert max-w-none">
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                If you have questions about these Terms of Service, please contact us:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-4">
                <li>Email: <a href="mailto:support@foxie.systems" className="text-primary-600 dark:text-primary-400 hover:underline">support@foxie.systems</a></li>
                <li>Contact Form: <Link to="/contact" className="text-primary-600 dark:text-primary-400 hover:underline">Visit our contact page</Link></li>
                <li>Help Center: <Link to="/help" className="text-primary-600 dark:text-primary-400 hover:underline">Browse our help articles</Link></li>
              </ul>
            </div>
          </section>
        </div>

        {/* Footer Links */}
        <div className="mt-8 flex flex-wrap gap-4 justify-center text-sm">
          <Link to="/privacy-policy" className="text-primary-600 dark:text-primary-400 hover:underline">
            Privacy Policy
          </Link>
          <span className="text-gray-400">•</span>
          <Link to="/refund-policy" className="text-primary-600 dark:text-primary-400 hover:underline">
            Refund Policy
          </Link>
          <span className="text-gray-400">•</span>
          <Link to="/contact" className="text-primary-600 dark:text-primary-400 hover:underline">
            Contact Us
          </Link>
        </div>
      </div>
    </div>
  );
}
